import React, {CSSProperties, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';

import {ReactComponent as NIcon} from '@/assets/icons/brand_list_n_icon.svg';
import LocationIcon from '@/components/brandListItem/components/LocationIcon';
import LocationIconRed from '@/components/brandListItem/components/LocationIconRed';
import useGeoLocation from '@/hooks/useGeoLocation';
import useImLogger from '@/hooks/useImLogger';
import useLogger, {Action} from '@/hooks/useLogger';

import styles from './styles.module.scss';
import Image from '../v2/ui/atoms/Image/Image';

interface Props {
  item: BrandListItemType;
  itemStyle?: CSSProperties;
  isCurrentLocation: boolean;
  isBlur?: boolean;
  isScrollend?: boolean;
  isDisabled?: boolean;
  onImageLoad?: () => void;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  customOnClick?: () => void;
}

// 스켈레톤 컴포넌트 정의
const ImageSkeleton = ({width, height}: {width: number; height: number}) => (
  <div
    style={{
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: '#f0f0f0',
      borderRadius: '4px',
      position: 'relative',
      overflow: 'hidden',
    }}>
    <div
      className={styles.skeletonShimmer}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background:
          'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 100%)',
        animation: 'shimmer 1.5s infinite',
      }}
    />
  </div>
);

export default function BrandListItem({
  item,
  itemStyle,
  isCurrentLocation,
  isBlur,
  isScrollend,
  isDisabled,
  onImageLoad,
  wrapperProps,
  customOnClick,
}: Props) {
  const navigator = useNavigate();
  const {calcWalkMinutes} = useGeoLocation();
  const {title, imageList: images, info} = item;
  const itemRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [imgWidth, setImgWidth] = useState(0);
  const [minImgWidth, setMinImgWidth] = useState(0);
  const sender = useImLogger();
  const log = useLogger();

  // 이미지 로딩 상태 관리
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);

  useEffect(() => {
    if (document.getElementById('main')) {
      const ratio = 0.624;
      const minRatio = 0.384;
      const _width =
        Math.ceil(
          (document.getElementById('main')?.offsetWidth || 0) * ratio,
        ) || 234;
      const _minWidth =
        Math.ceil(
          (document.getElementById('main')?.offsetWidth || 0) * minRatio,
        ) || 144;
      setImgWidth(_width);
      setMinImgWidth(_minWidth);
    }
  }, []);

  // 이미지 로딩 상태 초기화
  useEffect(() => {
    if (images) {
      setImagesLoaded(Array(Math.min(images.length, 5)).fill(false));
    }
  }, [images]);

  // 이미지 로드 핸들러
  const handleImageLoad = (index: number) => {
    console.log(`Image ${index} loaded for brand: ${title}`);
    setImagesLoaded(prev => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });

    // 상위 컴포넌트의 onImageLoad도 호출
    if (onImageLoad) {
      onImageLoad();
    }
  };

  // 이미지 오류 핸들러
  const handleImageError = (index: number) => {
    console.error(`Image ${index} failed to load for brand: ${title}`);
    // 로드 실패한 이미지를 기록하여 필요하면 추가 조치 가능
  };

  // 첫 번째와 두 번째 이미지가 로드되면 나머지 이미지 미리 로드 시작
  useEffect(() => {
    // 첫 번째와 두 번째 이미지가 모두 로드된 경우에만 나머지 이미지 로드 시작
    if ((imagesLoaded[0] || imagesLoaded[1]) && images && images.length > 2) {
      // 나머지 이미지 로드 시작
      const preloadImages = () => {
        images.slice(2, 5).forEach((imageSrc, idx) => {
          const index = idx + 2; // 인덱스 조정 (첫 번째, 두 번째 이미지 제외)
          if (!imagesLoaded[index]) {
            const img = new window.Image();
            img.src = imageSrc;
          }
        });
      };
      // 약간의 지연 후 미리 로드 시작
      setTimeout(preloadImages, 100);
    }
  }, [imagesLoaded, images]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const itemId = entry.target.getAttribute('data-id');
            const itemTitle = entry.target.getAttribute('data-title');
            const itemData = entry.target.getAttribute('data-item');
            const item = itemData ? JSON.parse(itemData) : null;

            console.log('BrandListItem 노출됨:', item);
            sender({
              _msg: itemTitle || '',
              _screen: window.location.pathname,
              _keyword: itemId?.toString(),
              _data: {to: item},
            });
            observerRef.current?.unobserve(entry.target); // 중복 호출 방지
          }
        });
      },
      {
        root: null, // 뷰포트를 기준으로 관찰
        threshold: [0.9], // 요소가 90% 이상 화면에 노출될 때만 트리거
      },
    );

    const observer = observerRef.current;

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={itemRef}
      data-id={item.id}
      data-title={title}
      data-item={JSON.stringify(item)}
      data-brand-id={item.id}
      className={styles.wrapper}
      onClick={() => {
        if (isDisabled) {
          return;
        }

        if (!isBlur) {
          if (customOnClick) {
            customOnClick();
            return;
          }

          console.log('BrandListItem 클릭됨:', item);
          log({
            _msg: 'BrandClick',
            _screen: window.location.pathname,
            _action: Action.CLICK,
            _keyword: item.id.toString(),
            _data: {to: item},
          });
          navigator(`/brand/${item.parentId}/${item.id}`, {
            state: {
              brand: item,
              prev: location.pathname.replace('/', ''),
              scrollEnabled: true,
            },
          });
        }
      }}
      style={itemStyle}
      {...wrapperProps}>
      {isBlur && (
        <div className={`${styles.not_login}`}>
          <div
            className={`${styles.anim_container} ${
              isScrollend ? styles.fade_in_up : ''
            }`}>
            <strong>
              단 3초 만에 로그인하고
              <br />더 많은 브랜드를 확인하세요.
            </strong>
            <button onClick={() => navigator('/login')}>
              <span>카카오계정으로 간편하게</span>
              로그인하고 모두 보기
            </button>
          </div>
        </div>
      )}
      <div
        className={styles.imgWrapper}
        style={{
          height: `${minImgWidth}px`,
          position: 'relative',
        }}>
        <div
          ref={carouselRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            minWidth: 'fit-content',
            height: '100%',
          }}>
          {images &&
            images.slice(0, 5).map((image, i) => (
              <div
                key={`image_container_${i}`}
                style={{
                  position: 'relative',
                  marginRight: i === 4 ? '20px' : '2px',
                  marginLeft: i === 0 ? '20px' : '0px',
                  width: i === 0 ? `${imgWidth}px` : `${minImgWidth}px`,
                  height: `${minImgWidth}px`,
                  flexShrink: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                {/* 스켈레톤 UI - 이미지가 로드 중일 때만 표시 */}
                {!imagesLoaded[i] && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 1,
                    }}>
                    <ImageSkeleton
                      width={i === 0 ? imgWidth : minImgWidth}
                      height={minImgWidth}
                    />
                  </div>
                )}

                {/* 실제 이미지 */}
                <div
                  className={`${styles.brandImageContainer} lazy-load-image-background blur lazy-load-image-loaded`}
                  style={{
                    color: 'transparent',
                  }}>
                  <Image
                    key={`image_ref_${i}`}
                    src={image}
                    width={i === 0 ? imgWidth : minImgWidth}
                    height={minImgWidth}
                    className={styles.brandImage}
                    style={{
                      zIndex: imagesLoaded[i] ? 2 : 0,
                    }}
                    onLoad={() => handleImageLoad(i)}
                    onError={() => handleImageError(i)}
                    priority={i === 0 || i === 1}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className={styles.titleWrapper} style={{margin: '12px 20px 0 20px'}}>
        <strong className="flex items-center">
          {title}
          {item.state === 'contracted' && <NIcon style={{marginLeft: '5px'}} />}
        </strong>
        {item.benefits && (
          <span className={styles.benefit}>{item.benefits}</span>
        )}
      </div>
      <div
        className={styles.locationWrapper}
        style={{margin: '8px 20px 0 20px'}}>
        {isCurrentLocation ? <LocationIconRed /> : <LocationIcon />}
        <span
          style={{
            marginLeft: '2px',
            color: '#000000',
          }}>{`${
          isCurrentLocation && calcWalkMinutes
            ? calcWalkMinutes(item.distance || 0)
            : item.locationInfo || ''
        } ∙ ${item.keyword || ''}`}</span>
      </div>
      <div className={styles.infoWrapper} style={{margin: '8px 20px 0 20px'}}>
        {info && info.replace(/\\n/g, ' ')}
      </div>
    </div>
  );
}
